import { captureException } from "@sentry/browser";
import React, { useEffect, useState } from "react";
import { t } from "ttag";

import * as financingAPI from "../../../api/financing";
import { ConfiguratorPrice } from "../../../common/ConfiguratorPrice";
import { ProductCompareTheme } from "../../../constants";
import { IProduct } from "../../../models/catalogue";
import { FinancingPlan } from "../../../models/financing";
import { IProductID, IWebPageURL } from "../../../models/nominals";
import { ICompareTile } from "../../../models/product-compare";
import { filterFinancingPlans, sortFinancingPlans } from "../../checkout/utils";
import { DiscountDisplayMode } from "../../configurator/constants";
import { getVariantPrice } from "../selectors";
import { ShopNowLink } from "./ShopNowLink";
import { TryInStoreCallout } from "./TryInStoreCallout";

import styles from "./ProductCompareTileCTA.module.scss";

interface IProps {
    data: ICompareTile;
    financingLink: IWebPageURL;
    selectedVariant: IProduct | null;
    showStartingAt: boolean;
    showInStoreCallout?: boolean;
    theme?: string | null;
}

export const ProductCompareTileCTA = (props: IProps): React.JSX.Element => {
    const [financingPlans, setFinancingPlans] = useState<FinancingPlan[]>([]);

    useEffect(() => {
        const fetchPlans = async () => {
            if (props.selectedVariant?.id) {
                const plans = await getVariantFinancingPlans(
                    props.selectedVariant.id,
                );
                if (plans) {
                    setFinancingPlans(plans);
                }
            }
        };
        fetchPlans();
    }, [props.selectedVariant]);

    const isStepUpOrSelect = (): boolean => {
        return (
            props.theme === ProductCompareTheme.STEP_UP ||
            props.theme === ProductCompareTheme.SELECT
        );
    };

    const getVariantFinancingPlans = async (
        productID: IProductID,
    ): Promise<FinancingPlan[]> => {
        // Load Financing plans depending on the product
        const plans = await financingAPI
            .listFinancingPlansForProduct(productID)
            .catch((err) => {
                captureException(err);
                return [];
            });
        const filteredPlans = filterFinancingPlans(plans);
        return sortFinancingPlans(filteredPlans);
    };

    const buildPrice = (): React.JSX.Element => {
        if (props.selectedVariant) {
            return (
                <>
                    <ConfiguratorPrice
                        price={getVariantPrice(props.selectedVariant)}
                        variant={props.selectedVariant}
                        selectedAddonPrice={null}
                        financingLink={props.financingLink}
                        strikeThroughMSRP={false}
                        discountDisplayMode={DiscountDisplayMode.DISABLED}
                        showStartingAt={props.showStartingAt}
                        actualPriceStyle={""}
                        addPriceText={
                            props.theme === ProductCompareTheme.THEME_ONE
                        }
                        financingPlans={financingPlans}
                    />
                </>
            );
        }
        if (props.theme === ProductCompareTheme.WITHOUT_COMPARE) {
            return (
                <div className="configurator__price">
                    <p className="type_x0point75">{t`Unavailable`}</p>
                </div>
            );
        }
        return (
            <div className="configurator__price">
                <p className="type_x0point75">{t`Unavailable in this size`}</p>
            </div>
        );
    };

    return (
        <div
            className={`product-compare-tile-cta ${styles.root} ${props.theme}`}
        >
            {buildPrice()}
            {props.showInStoreCallout && <TryInStoreCallout />}
            {isStepUpOrSelect() || (
                <ShopNowLink
                    tile={props.data}
                    selectedVariant={props.selectedVariant}
                />
            )}
        </div>
    );
};
